import { Link } from "react-router";
import { ArrowLeft, Upload, FileSpreadsheet, Download, HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";
import { downloadFileByUrl } from "@/lib/utils";

const csvFields = [
  {
    name: "spec_name",
    type: "string",
    required: true,
    description: "The name of the spec",
    example: "GE_300",
  },
  {
    name: "report_name",
    type: "string",
    required: true,
    description: "The name of the report",
    example: "GE_300",
  },
  {
    name: "dut_name",
    type: "string",
    required: true,
    description: "Device Under Test name - unique identifier for the structure",
    example: "GE_300",
  },
  {
    name: "condition",
    type: "string",
    required: true,
    description: "Condition of the device being tested",
    example: "2+2_newPOR",
  },
  {
    name: "device_class",
    type: "string",
    required: true,
    description: "Classification of the device being tested",
    example: "PCM",
  },
  {
    name: "recipe",
    type: "string",
    required: true,
    description: "The recipe that's linked to the spec",
    example: "LossMeasurement",
  },
  {
    name: "lsl",
    type: "number",
    required: false,
    description: "LSL value",
    example: "0.1",
  },
  {
    name: "tgt",
    type: "number",
    required: false,
    description: "TGT value",
    example: "0.1",
  },
  {
    name: "usl",
    type: "number",
    required: false,
    description: "USL value",
    example: "0.1",
  },
  {
    name: "jmp_ymin",
    type: "number",
    required: false,
    description: "JMP_YMIN value",
    example: "0.1",
  },
  {
    name: "jmp_ymax",
    type: "number",
    required: false,
    description: "JMP_YMAX value",
    example: "0.1",
  },
  {
    name: "jmp_yinc",
    type: "number",
    required: false,
    description: "JMP_YINC value",
    example: "0.1",
  },
  {
    name: "lib_median",
    type: "number",
    required: false,
    description: "LIB_MEDIAN value",
    example: "0.1",
  },
  {
    name: "lib_mean",
    type: "number",
    required: false,
    description: "LIB_MEAN value",
    example: "0.1",
  },
  {
    name: "lib_std",
    type: "number",
    required: false,
    description: "LIB_STD value",
    example: "0.1",
  },
  {
    name: "lib_q90",
    type: "number",
    required: false,
    description: "LIB_Q90 value",
    example: "0.1",
  },
  {
    name: "lib_q10",
    type: "number",
    required: false,
    description: "LIB_Q10 value",
    example: "0.1",
  },
  {
    name: "lib_n",
    type: "number",
    required: false,
    description: "LIB_N value",
    example: "0.1",
  },
];

export function CustomSpecListCsvDocPage() {
  const handleDownloadExample = () => {
    downloadFileByUrl("/example_custom_specs.csv", "example_custom_specs.csv");
  };

  return (
    <>
      <Header />
      <Main>
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/docs" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>

          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-linear-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
              <Upload className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Custom Spec List CSV Format</h1>
              <p className="text-xl text-gray-600">Reference guide for CSV upload</p>
            </div>
          </div>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              A custom spec list allows you to create a specification list by uploading a CSV file
              instead of selecting specs from a master spec list. This is useful when you have
              pre-defined measurement configurations or want to import specs from external sources.
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <h4 className="font-semibold text-green-900 mb-2">When to use Custom CSV Upload:</h4>
              <ul className="text-green-800 text-sm space-y-1">
                <li>• You have measurement configurations from external tools or systems</li>
                <li>• You want to bulk-create specs with specific parameters</li>
                <li>• You need to import legacy spec definitions</li>
                <li>• The specs you need are not available in any master spec list</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <FileSpreadsheet className="w-5 h-5 mr-2" />
                Example CSV File
              </div>
              <Button onClick={handleDownloadExample} size="sm">
                <Download className="w-4 h-4 mr-2" />
                Download Example
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              Download the example CSV file to see the expected format. The file contains sample
              data that demonstrates how to structure your custom spec list.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg border overflow-x-auto">
              <code className="text-xs text-gray-600 whitespace-pre">
                spec_name,report_name,dut_name,condition,device_class,recipe,lsl,tgt,...
                {"\n"}
                GE_300,GE_300,GE_300,POR,L,LossMeasurement,352.8,135,,3137.2,-135,...
              </code>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <HelpCircle className="w-5 h-5 mr-2" />
              CSV Field Reference
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              The table below describes all available fields in the CSV file. Required fields are
              marked with a badge.
            </p>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Field Name</TableHead>
                    <TableHead className="w-[100px]">Type</TableHead>
                    <TableHead className="w-[100px]">Required</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[120px]">Example</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvFields.map((field) => (
                    <TableRow key={field.name}>
                      <TableCell className="font-mono text-sm">{field.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {field.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {field.required ? (
                          <Badge variant="destructive" className="text-xs">
                            Required
                          </Badge>
                        ) : (
                          <span className="text-gray-400 text-sm">Optional</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-700">{field.description}</TableCell>
                      <TableCell className="font-mono text-xs text-gray-500">
                        {field.example || <span className="text-gray-300">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Currently Supported Recipes</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>BERMeasurement</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>DetectorMeasurement</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>EAMMeasurement</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>EyeDiagramMeasurement</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>FiberAttachMeasurement</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>IVMeasurement</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>LIVMeasurement</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>LossMeasurement</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>MirrorMeasurement</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>ModulatorMeasurement</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>OFTRMeasurement</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>SParamMeasurement</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Data Types</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg border">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Badge variant="outline" className="mr-2">
                    string
                  </Badge>
                  Text Values
                </h4>
                <p className="text-sm text-gray-600">
                  Any text value. Can be empty. Do not wrap in quotes unless the value contains
                  commas.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Badge variant="outline" className="mr-2">
                    number
                  </Badge>
                  Numeric Values
                </h4>
                <p className="text-sm text-gray-600">
                  Decimal numbers. Use period (.) as decimal separator. Can be negative.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Tips & Best Practices</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>
                  Always include the header row with field names exactly as specified above
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Use comma (,) as the field delimiter</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>
                  Ensure <code className="bg-gray-100 px-1 rounded">spec_name</code>,{" "}
                  <code className="bg-gray-100 px-1 rounded">report_name</code>,{" "}
                  <code className="bg-gray-100 px-1 rounded">dut_names</code>,{" "}
                  <code className="bg-gray-100 px-1 rounded">condition</code> and{" "}
                  <code className="bg-gray-100 px-1 rounded">device_class</code> are always filled
                  in
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Save the file with UTF-8 encoding to support special characters</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 mr-2">!</span>
                <span>
                  If a value contains commas, wrap the entire value in double quotes (e.g.,{" "}
                  <code className="bg-gray-100 px-1 rounded">"value, with, commas"</code>)
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 mr-2">!</span>
                <span>Empty cells are allowed for optional fields - just leave them blank</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button asChild variant="outline">
            <Link to="/docs">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/docs/mask-workflow">Continue to the Mask Workflow</Link>
          </Button>
        </div>
      </Main>
    </>
  );
}
